import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * CustomSelect (no width management)
 * - Fully styleable trigger + menu (CSS controls size)
 * - Keyboard: ArrowUp/Down, Home/End, Enter/Space select, Esc close
 * - Click outside to close
 * - Bublé-safe: no optional chaining, no nullish coalescing, no for..of
 *
 * Expected classNames for styling:
 *  .c-select (root), .c-select__trigger (button), .c-select__menu (listbox), .c-select__option (option)
 */
const CustomSelect = function (props) {
  var containerRef = useRef(null);
  var menuRef = useRef(null);

  var [open, setOpen] = useState(false);
  var [highlightIndex, setHighlightIndex] = useState(-1);

  // Derive selected label
  var selectedLabel = (function () {
    if (!props.options) return "";
    for (var i = 0; i < props.options.length; i++) {
      var opt = props.options[i];
      if (opt && opt.value === props.value) return opt.label || "";
    }
    return "";
  })();

  // Close on outside click
  useEffect(function () {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return function () { document.removeEventListener("mousedown", onDocClick); };
  }, []);

  // When opening, highlight selected or first enabled
  useEffect(function () {
    if (!open || !props.options) return;
    var idx = -1;
    for (var i = 0; i < props.options.length; i++) {
      var o = props.options[i];
      if (o && o.value === props.value && !o.disabled) { idx = i; break; }
    }
    if (idx === -1) {
      for (var j = 0; j < props.options.length; j++) {
        var p = props.options[j];
        if (p && !p.disabled) { idx = j; break; }
      }
    }
    setHighlightIndex(idx);
  }, [open, props.value, props.options]);

  function closeMenu() { if (open) setOpen(false); }

  function onTriggerKeyDown(e) {
    var key = e.key;
    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true); else moveHighlight(key === "ArrowDown" ? 1 : -1);
    } else if (key === "Enter" || key === " ") {
      e.preventDefault();
      setOpen(!open);
    } else if (key === "Escape") {
      closeMenu();
    }
  }

  function onMenuKeyDown(e) {
    var key = e.key;
    if (key === "ArrowDown") { e.preventDefault(); moveHighlight(1); }
    else if (key === "ArrowUp") { e.preventDefault(); moveHighlight(-1); }
    else if (key === "Home")   { e.preventDefault(); moveToEdge("start"); }
    else if (key === "End")    { e.preventDefault(); moveToEdge("end"); }
    else if (key === "Enter" || key === " ") {
      e.preventDefault(); commitHighlight();
    } else if (key === "Escape") {
      e.preventDefault(); closeMenu();
    }
  }

  function moveHighlight(delta) {
    if (!props.options || !props.options.length) return;
    var idx = highlightIndex;
    var count = props.options.length;
    for (var k = 0; k < count; k++) {
      idx = (idx + delta + count) % count;
      var o = props.options[idx];
      if (o && !o.disabled) { setHighlightIndex(idx); break; }
    }
  }

  function moveToEdge(edge) {
    if (!props.options || !props.options.length) return;
    var count = props.options.length;
    if (edge === "start") {
      for (var i = 0; i < count; i++) {
        var o = props.options[i];
        if (o && !o.disabled) { setHighlightIndex(i); break; }
      }
    } else {
      for (var j = count - 1; j >= 0; j--) {
        var p = props.options[j];
        if (p && !p.disabled) { setHighlightIndex(j); break; }
      }
    }
  }

  function commitHighlight() {
    if (highlightIndex < 0 || !props.options) return;
    var opt = props.options[highlightIndex];
    if (!opt || opt.disabled) return;
    props.onChange(opt.value);
    closeMenu();
  }

  function onOptionClick(idx) {
    if (!props.options) return;
    var opt = props.options[idx];
    if (!opt || opt.disabled) return;
    props.onChange(opt.value);
    closeMenu();
  }

  var activeDescendant = (open && highlightIndex >= 0) ? (props.id || "cs") + "-opt-" + highlightIndex : undefined;
  var listboxId = (props.id || "cs") + "-listbox";

  return (
    <div
      ref={containerRef}
      className={props.className || "c-select"}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        id={props.id}
        className={props.triggerClassName || "c-select__trigger"}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        aria-controls={listboxId}
        aria-label={props.ariaLabel || undefined}
        onClick={function () { setOpen(!open); }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="c-select__value">
          {selectedLabel || props.placeholder || "Select"}
        </span>
        {/* If your CSS already adds a caret, keep this commented out */}
        {/* <span className="c-select__icon" aria-hidden="true">▾</span> */}
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="listbox"
          id={listboxId}
          tabIndex={-1}
          className={props.menuClassName || "c-select__menu"}
          aria-activedescendant={activeDescendant}
          onKeyDown={onMenuKeyDown}
        >
          {props.options && props.options.map(function (opt, idx) {
            var isSelected = opt && (opt.value === props.value);
            var isHighlighted = idx === highlightIndex;
            var isDisabled = !!(opt && opt.disabled);
            var classes = (props.optionClassName || "c-select__option") +
              (isSelected ? " is-selected" : "") +
              (isHighlighted ? " is-highlighted" : "") +
              (isDisabled ? " is-disabled" : "");
            var label = opt && opt.label ? opt.label : "";

            return (
              <div
                key={(opt ? opt.value : "opt") + "-" + idx}
                id={(props.id || "cs") + "-opt-" + idx}
                role="option"
                aria-selected={isSelected ? "true" : "false"}
                aria-disabled={isDisabled ? "true" : "false"}
                className={classes}
                onMouseEnter={function () { if (!isDisabled) setHighlightIndex(idx); }}
                onMouseDown={function (e) { e.preventDefault(); }}
                onClick={function () { if (!isDisabled) onOptionClick(idx); }}
              >
                {label}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

CustomSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  triggerClassName: PropTypes.string,
  menuClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string
};

export default CustomSelect;
